import {
  CheckedState,
  asyncDataLoaderFeature,
  checkboxesFeature,
  hotkeysCoreFeature,
  selectionFeature
} from '@headless-tree/core'
import type { ChildrenWithData, TreeItem } from '@/api/content/connector'
import { FileSyncState } from '@/api/content/connector'
import {
  checkConnectorTreeSelectionItem,
  createConnectorTreeSelectionModel,
  getConnectorTreeSelectionState,
  loadConnectorTreeSelectionItem,
  serializeConnectorTreeSelection,
  uncheckConnectorTreeSelectionItem
} from './connectorFileTreeSelection'
import type { FeatureImplementation, ItemInstance, TreeConfig, TreeDataLoader, TreeInstance } from '@headless-tree/core'
import type { ConnectorTreeSelectionModel } from './connectorFileTreeSelection'

export const TREE_ROOT_NAME = 'root'

export type TreeNodes = Record<string, TreeItem>

export function getTreeConfig({
  asyncDataLoader
}: {
  asyncDataLoader: TreeDataLoader<TreeItem>
}): TreeConfig<TreeItem> {
  return {
    rootItemId: TREE_ROOT_NAME,
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => item.getItemData().isFolder,
    dataLoader: asyncDataLoader,
    createLoadingItemData: () => ({
      id: 'loading',
      name: 'Loading...',
      isFolder: false,
      children: [],
      checked: CheckedState.Unchecked,
      syncState: FileSyncState.Unknown
    }),
    canCheckFolders: true,
    propagateCheckedState: false,
    features: [
      asyncDataLoaderFeature,
      selectionFeature,
      hotkeysCoreFeature,
      checkboxesFeature,
      createCheckboxOverride()
    ]
  }
}

type SelectionController = {
  getModel: () => ConnectorTreeSelectionModel
  registerLoadedSubtree: (item: ItemInstance<TreeItem>) => void
}

const selectionControllersByTree = new WeakMap<TreeInstance<TreeItem>, SelectionController>()

// Headless Tree adapter for the immutable lazy-selection model:
// 1. Checking a folder marks only the folder as Checked — descendants inherit when loaded (expanded)
// 2. Unchecking a folder unchecks only loaded descendants
// 3. Checking all sub items within a folder does NOT check the folder itself
// 4. If some sub items are checked, folder is Indeterminate
// 5. Folders are only Checked if user explicitly checks them
function createCheckboxOverride(): FeatureImplementation<TreeItem> {
  let model = createConnectorTreeSelectionModel()

  function loadedChildren(item: ItemInstance<TreeItem>): Array<ItemInstance<TreeItem>> {
    const tree = item.getTree()
    return tree.retrieveChildrenIds(item.getId(), true).map((id) => tree.getItemInstance(id))
  }

  function registerItem(item: ItemInstance<TreeItem>) {
    const parent = item.getParent()
    if (parent && !model.nodes.has(parent.getId())) registerItem(parent)
    model = loadConnectorTreeSelectionItem(model, item.getItemData(), parent?.getId() ?? null)
  }

  function registerLoadedSubtree(item: ItemInstance<TreeItem>) {
    registerItem(item)
    for (const child of loadedChildren(item)) registerLoadedSubtree(child)
  }

  function registerController(tree: TreeInstance<TreeItem>) {
    selectionControllersByTree.set(tree, { getModel: () => model, registerLoadedSubtree })
  }

  function syncLoadedTree(tree: TreeInstance<TreeItem>) {
    const checkedItemIds: Array<string> = []
    function sync(item: ItemInstance<TreeItem>) {
      registerItem(item)
      const state = getConnectorTreeSelectionState(model, item.getId())
      item.getItemData().checked = state
      if (state === CheckedState.Checked) checkedItemIds.push(item.getId())
      for (const child of loadedChildren(item)) sync(child)
    }
    sync(tree.getRootItem())
    tree.applySubStateUpdate('checkedItems', () => checkedItemIds)
  }

  return {
    itemInstance: {
      getCheckedState: ({ tree, item }) => {
        registerController(tree)
        registerItem(item)
        return getConnectorTreeSelectionState(model, item.getId())
      },

      setChecked: ({ tree, item }) => {
        registerController(tree)
        registerLoadedSubtree(tree.getRootItem())
        model = checkConnectorTreeSelectionItem(model, item.getId())
        syncLoadedTree(tree)
      },

      setUnchecked: ({ tree, item }) => {
        registerController(tree)
        registerLoadedSubtree(tree.getRootItem())
        model = uncheckConnectorTreeSelectionItem(model, item.getId())
        syncLoadedTree(tree)
      }
    }
  }
}

export function getTreeStateCheckedAndIndeterminateItems(tree: TreeInstance<TreeItem>): TreeNodes {
  const root = tree.getRootItem()

  function registerLoadedItems(item: ItemInstance<TreeItem>) {
    item.getCheckedState()
    for (const childId of tree.retrieveChildrenIds(item.getId(), true)) {
      registerLoadedItems(tree.getItemInstance(childId))
    }
  }
  registerLoadedItems(root)

  const controller = selectionControllersByTree.get(tree)
  if (!controller) throw new Error('Connector tree selection adapter was not registered')
  controller.registerLoadedSubtree(root)
  return serializeConnectorTreeSelection(controller.getModel(), TREE_ROOT_NAME)
}

function staticDataGetChildrenWithData(tree: TreeNodes, itemId: string): ChildrenWithData {
  const children = tree[itemId]?.children ?? []
  const childrenWithData: ChildrenWithData = []
  for (const childId of children) {
    const data = tree[childId]
    if (data) childrenWithData.push({ id: childId, data })
  }
  return childrenWithData
}

export function staticDataAsyncDataLoader(treeData: TreeNodes): TreeDataLoader<TreeItem> {
  return {
    getItem: (itemId: string) => {
      const item = treeData[itemId]
      if (!item) throw new Error(`Tree item not found: ${itemId}`)
      return Promise.resolve(item)
    },
    getChildrenWithData: (itemId: string) => Promise.resolve(staticDataGetChildrenWithData(treeData, itemId))
  }
}
